import { mm, type Um } from './units.js';
import type { GrainDirection, MaterialSlot, Vec3 } from './model.js';
import type { CabinetParams } from './params.js';
import type { EdgeBandSpec, EdgeOverrides, EdgePolicyId, Environment } from './edgebanding.js';
import type { DimensionLink } from './links.js';
import type { Pin } from './pins.js';

export const CURRENT_SCHEMA_VERSION = 4;

export type QuarterTurn = 0 | 90 | 180 | 270;

export interface Transform {
  /** World placement of the unit origin (left/back/floor corner) in room space, µm. */
  position: Vec3;
  rotationY: QuarterTurn;
  mirrored: boolean;
}

/** Link of an instance to an immutable preset version. `values` are the instance's preset parameter values. */
export interface PresetLink {
  presetId: string;
  versionId: string;
  version: number;
  values: Record<string, number>;
}

export interface CabinetInstance {
  id: string;
  name: string;
  params: CabinetParams;
  transform: Transform;
  /** Material decor ids per slot; falls back to document defaults. */
  materials: Partial<Record<MaterialSlot, string>>;
  edgeOverrides: EdgeOverrides;
  hidden: boolean;
  locked: boolean;
  preset: PresetLink | null;
  /** Wet areas (kitchen sink zone, bathroom) seal exposed cuts. */
  environment: Environment;
  /** Anchor point kept fixed when dimensions change (default: min corner). */
  pin?: Pin;
}

export type PanelOrientation = 'horizontal' | 'vertical-side' | 'vertical-front';

/** A free panel of a custom assembly, positioned in the assembly's local frame. */
export interface AssemblyPanel {
  key: string;
  label: string;
  orientation: PanelOrientation;
  length: Um;
  width: Um;
  thickness: Um;
  /** Min corner in assembly-local coordinates. */
  position: Vec3;
  materialSlot: MaterialSlot;
  grain: GrainDirection;
  /** Optional repetition along an axis (e.g. shelves): count copies, `spacing` between copy origins. */
  repeat: { count: number; axis: 'x' | 'y' | 'z'; spacing: Um } | null;
  /** Intentional joints with other panels (by key), e.g. a back panel sitting in grooves. Excluded from collision checks. */
  joints: { key: string; kind: 'groove' | 'rebate' }[];
  /** Anchor point kept fixed when length/width/thickness change (default: min corner). */
  pin?: Pin;
}

/**
 * A parametric group of shelves between two boundary panels of an assembly ("Police"). The shelves
 * are generated (evenly spaced) on every evaluation; their part keys are `${id}-${n}`.
 */
export interface ShelfSet {
  id: string;
  label: string;
  bottomKey: string;
  topKey: string;
  /** Boundary whose width/depth the shelves take (the panel picked second). */
  refKey: string;
  /** Vertical panels bounding the compartment (null = edge of the boundary panel, no clearance). */
  leftKey: string | null;
  rightKey: string | null;
  count: number;
  /** Clearance to each side panel. */
  gap: Um;
  thickness: Um;
  /** Shelf numbers (1-based) removed by the user; "Vrati obrisane" clears this. */
  deleted: number[];
  /** Per shelf number: how much shorter in depth (from the front), e.g. behind an inset front. */
  setbacks?: Record<string, Um>;
  materialSlot: MaterialSlot;
  grain: GrainDirection;
}

/** Rectangle in the assembly's front view (X/Y, local µm). */
export interface FrontRect {
  x0: Um;
  y0: Um;
  x1: Um;
  y1: Um;
}

/** An opening a front covers: the panels around it (null = no panel) and its size when picked. */
export interface FrontSpaceRef {
  left: string | null;
  right: string | null;
  bottom: string | null;
  top: string | null;
  rect: FrontRect;
}

/** A front (door/flap) over one or several openings of a free-panel assembly. */
export interface Front {
  id: string;
  label: string;
  spaces: FrontSpaceRef[];
  /** Overlay covers the panel edges; inset sits inside the opening. */
  mode: 'overlay' | 'inset';
  /** Clearance (zazor) to the carcass edge / the opening; default 2 mm. The gap between fronts (fuga) is `AssemblyInstance.frontReveal`. */
  clearance?: Um;
  thickness: Um;
  /** Hinge side; null = not chosen yet. */
  hinge: 'left' | 'right' | 'top' | 'bottom' | 'none' | null;
  /** Part of a split front: fractions [start, end] of the whole front per axis (x = left→right, y = bottom→top). */
  slice?: { x?: [number, number]; y?: [number, number] };
  materialSlot: MaterialSlot;
  grain: GrainDirection;
}

/**
 * Custom assembly built from individual panels ("pravljenje od nule" / static preset).
 * No parametric rules are invented for it — dimensions change per panel only.
 */
export interface AssemblyInstance {
  id: string;
  name: string;
  panels: AssemblyPanel[];
  transform: Transform;
  materials: Partial<Record<MaterialSlot, string>>;
  edgeOverrides: EdgeOverrides;
  hidden: boolean;
  locked: boolean;
  preset: { presetId: string; versionId: string; version: number } | null;
  environment: Environment;
  /** Shelf sets between panels of this assembly. */
  shelfSets?: ShelfSet[];
  /** Fronts over openings of this assembly. */
  fronts?: Front[];
  /** Gap between neighbouring fronts (fuga) of this assembly's front group; default 3 mm. */
  frontReveal?: Um;
}

export interface ProjectDocument {
  schemaVersion: typeof CURRENT_SCHEMA_VERSION;
  settings: {
    currency: 'BAM' | 'EUR';
    edgePolicy: EdgePolicyId;
    measurementProfileId: string;
    /** Edge bands used by automatic decisions (catalog product optional until chosen). */
    edgeBands: { standard: EdgeBandSpec; front: EdgeBandSpec };
  };
  room: { width: Um; depth: Um; height: Um };
  materials: Record<MaterialSlot, string>;
  cabinets: CabinetInstance[];
  assemblies: AssemblyInstance[];
  /** Dimension links ("link" / "link and keep difference"). */
  links: DimensionLink[];
}

export function emptyDocument(): ProjectDocument {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    settings: {
      currency: 'BAM',
      edgePolicy: 'workshop-standard',
      measurementProfileId: 'order-finished',
      edgeBands: { standard: { productId: null, thickness: mm(0.8), width: mm(22) }, front: { productId: null, thickness: mm(2), width: mm(22) } },
    },
    room: { width: mm(4000), depth: mm(3000), height: mm(2600) },
    materials: {
      carcass: 'demo-white-matt',
      back: 'demo-hdf-white',
      front: 'demo-oak-natural',
      'drawer-box': 'demo-white-matt',
      'drawer-bottom': 'demo-hdf-white',
      plinth: 'demo-anthracite',
    },
    cabinets: [],
    assemblies: [],
    links: [],
  };
}

/** Migrates any older stored document shape to the current schema (pure; input is not mutated). */
export function migrateDocument(raw: unknown): ProjectDocument {
  if (!raw || typeof raw !== 'object') throw new Error('Neispravan projektni dokument.');
  let doc = structuredClone(raw) as Record<string, unknown>;
  if (doc.schemaVersion === 1) {
    // v1 → v2: presets got version + values; custom assemblies were added.
    const cabinets = (doc.cabinets as Record<string, unknown>[] | undefined) ?? [];
    for (const c of cabinets) {
      const p = c.preset as { presetId: string; versionId: string } | null | undefined;
      c.preset = p ? { presetId: p.presetId, versionId: p.versionId, version: 1, values: {} } : null;
    }
    doc = { ...doc, schemaVersion: 2, cabinets, assemblies: [] };
  }
  if (doc.schemaVersion === 2) {
    // v2 → v3: unit environment (dry/wet) and project edge band choice.
    for (const u of [...((doc.cabinets as Record<string, unknown>[]) ?? []), ...((doc.assemblies as Record<string, unknown>[]) ?? [])]) u.environment = 'dry';
    const settings = { ...(doc.settings as Record<string, unknown>) };
    settings.edgeBands = { standard: { productId: null, thickness: mm(0.8), width: mm(22) }, front: { productId: null, thickness: mm(2), width: mm(22) } };
    doc = { ...doc, schemaVersion: 3, settings };
  }
  if (doc.schemaVersion === 3) {
    // v3 → v4: dimension links.
    doc = { ...doc, schemaVersion: 4, links: [] };
  }
  if (doc.schemaVersion === CURRENT_SCHEMA_VERSION) return doc as unknown as ProjectDocument;
  throw new Error(`Nepodržana verzija dokumenta: ${String(doc.schemaVersion)}`);
}
